import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import type { 
  JogoBichoResultado, 
  JogoBichoConfig, 
  JogoGerado, 
  ResultadoFormData, 
  ConfigFormData 
} from "@/types/softwareTurbinado";

export const useSoftwareTurbinado = () => {
  const { user } = useAuth();
  const [resultados, setResultados] = useState<JogoBichoResultado[]>([]);
  const [config, setConfig] = useState<JogoBichoConfig | null>(null);
  const [jogosGerados, setJogosGerados] = useState<JogoGerado | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showJogosModal, setShowJogosModal] = useState(false);
  const [selectedDays, setSelectedDays] = useState(5);
  const [isTrialExpired, setIsTrialExpired] = useState(false);

  // Carregar dados iniciais
  useEffect(() => {
    if (user?.id) {
      loadResultados();
      loadConfig();
      checkTrialPeriod();
    }
  }, [user?.id, selectedDays]);

  const checkTrialPeriod = async () => {
    if (!user?.id) return;

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("created_at, is_subscribed, plan")
        .eq("id", user.id)
        .single();

      if (profile && !profile.is_subscribed && profile.plan === 'free') {
        const createdAt = new Date(profile.created_at);
        const threeDaysLater = new Date(createdAt.getTime() + (3 * 24 * 60 * 60 * 1000));
        const twoDaysLater = new Date(createdAt.getTime() + (2 * 24 * 60 * 60 * 1000));
        const now = new Date();

        console.log('Verificando período de teste:', {
          created_at: createdAt,
          threeDaysLater,
          twoDaysLater,
          now,
          isExpired: now > threeDaysLater,
          shouldWarn: now > twoDaysLater
        });

        if (now > threeDaysLater) {
          console.log('Período de teste expirado, limitando acesso');
          setIsTrialExpired(true);
          
          await supabase
            .from("notifications")
            .insert({
              user_id: user.id,
              type: "warning",
              message: "Seu período de teste de 3 dias expirou. Você pode continuar usando o sistema, mas sem acesso aos palpites. Faça upgrade para ter acesso completo!",
              category: "trial_expired"
            });

          toast.warning("Período de teste expirado. Faça upgrade para ter acesso aos palpites!", {
            duration: 8000,
          });
          
        } else if (now > twoDaysLater) {
          console.log('Período de teste terminando, enviando aviso');
          
          const hoursLeft = Math.ceil((threeDaysLater.getTime() - now.getTime()) / (1000 * 60 * 60));
          
          await supabase
            .from("notifications")
            .insert({
              user_id: user.id,
              type: "warning",
              message: `Seu período de teste termina em ${hoursLeft} horas! Faça upgrade para continuar usando o Software Turbinado sem interrupções.`,
              category: "trial_warning"
            });

          toast.warning(`Período de teste termina em ${hoursLeft} horas. Faça upgrade!`);
        } else {
          setIsTrialExpired(false);
        }
      } else {
        setIsTrialExpired(false);
      }

    } catch (error) {
      console.error("Erro ao verificar período de teste:", error);
    }
  };

  const loadResultados = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from("jogo_bicho_resultados")
        .select("*")
        .eq("user_id", user.id)
        .order("data", { ascending: false })
        .limit(selectedDays);

      if (error) throw error;
      setResultados(data || []);
    } catch (error) {
      console.error("Erro ao carregar resultados:", error);
      toast.error("Erro ao carregar resultados");
    }
  };

  const loadConfig = async () => {
    if (!user?.id) return;

    try {
      console.log('Carregando configuração para usuário:', user.id);
      const { data, error } = await supabase
        .from("jogo_bicho_config")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      
      console.log('Configuração carregada:', data);
      setConfig(data);
    } catch (error) {
      console.error("Erro ao carregar configuração:", error);
    }
  };

  const saveResultado = async (dados: ResultadoFormData) => {
    if (!user?.id) return false;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("jogo_bicho_resultados")
        .upsert({
          user_id: user.id,
          ...dados
        });

      if (error) throw error;

      toast.success("Resultado salvo com sucesso!");
      await loadResultados();
      return true;
    } catch (error) {
      console.error("Erro ao salvar resultado:", error);
      toast.error("Erro ao salvar resultado");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const saveMultipleResultados = async (resultados: ResultadoFormData[]) => {
    if (!user?.id) return false;

    setIsLoading(true);
    try {
      const resultadosLimitados = resultados.slice(0, selectedDays);
      
      const resultadosComUserId = resultadosLimitados.map(resultado => ({
        user_id: user.id,
        ...resultado
      }));

      const { error } = await supabase
        .from("jogo_bicho_resultados")
        .upsert(resultadosComUserId);

      if (error) throw error;

      toast.success(`${resultadosLimitados.length} resultados salvos com sucesso!`);
      await loadResultados();
      return true;
    } catch (error) {
      console.error("Erro ao salvar resultados:", error);
      toast.error("Erro ao salvar resultados");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const saveConfig = async (dados: ConfigFormData) => {
    if (!user?.id) {
      console.error('Usuário não autenticado');
      toast.error("Usuário não autenticado");
      return false;
    }

    console.log('Iniciando salvamento da configuração:', dados);
    setIsLoading(true);
    
    try {
      const { data: existingConfig, error: selectError } = await supabase
        .from("jogo_bicho_config")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (selectError && selectError.code !== "PGRST116") {
        console.error('Erro ao verificar configuração existente:', selectError);
        throw selectError;
      }

      let result;
      
      if (existingConfig) {
        console.log('Atualizando configuração existente:', existingConfig.id);
        result = await supabase
          .from("jogo_bicho_config")
          .update({
            titulo_ppt: dados.titulo_ppt,
            titulo_ptm: dados.titulo_ptm,
            titulo_pt: dados.titulo_pt,
            titulo_ptv: dados.titulo_ptv,
            titulo_ptn: dados.titulo_ptn,
            titulo_coruja: dados.titulo_coruja,
            estado: dados.estado,
            loteria: dados.loteria,
            updated_at: new Date().toISOString()
          })
          .eq("user_id", user.id)
          .select()
          .single();
      } else {
        console.log('Criando nova configuração para usuário:', user.id);
        result = await supabase
          .from("jogo_bicho_config")
          .insert({
            user_id: user.id,
            titulo_ppt: dados.titulo_ppt,
            titulo_ptm: dados.titulo_ptm,
            titulo_pt: dados.titulo_pt,
            titulo_ptv: dados.titulo_ptv,
            titulo_ptn: dados.titulo_ptn,
            titulo_coruja: dados.titulo_coruja,
            estado: dados.estado,
            loteria: dados.loteria
          })
          .select()
          .single();
      }

      if (result.error) {
        console.error('Erro ao salvar configuração:', result.error);
        throw result.error;
      }

      console.log('Configuração salva com sucesso:', result.data);
      
      // Atualizar estado local imediatamente
      setConfig(result.data);
      
      toast.success("Configuração salva com sucesso! Os títulos foram atualizados.");
      
      return true;
      
    } catch (error) {
      console.error("Erro detalhado ao salvar configuração:", error);
      toast.error("Erro ao salvar configuração. Tente novamente.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const gerarJogos = async () => {
    if (!user?.id || resultados.length < selectedDays) {
      toast.error(`É necessário ter pelo menos ${selectedDays} dias de resultados para gerar jogos`);
      return false;
    }

    if (isTrialExpired) {
      toast.error("Seu período de teste de 3 dias expirou! Faça upgrade para gerar jogos.", {
        duration: 6000,
      });
      return false;
    }

    setIsLoading(true);
    try {
      const milhar = gerarMilhar(resultados, 12);
      const centenas = gerarCentenas(resultados, 12);
      const dezenas = gerarDezenas(resultados, 7);
      const ternosDezenas = gerarTernosDezenas(resultados, 7);
      const duquesDezenas = gerarDuquesDezenas(resultados, 7);
      const duplasGrupo = gerarDuplasGrupo(resultados, 7);
      const ternosGrupo = gerarTernosGrupo(resultados, 7);

      const jogoGerado: Omit<JogoGerado, 'id' | 'created_at'> = {
        user_id: user.id,
        milhar,
        centenas,
        dezenas,
        terno_dezenas: ternosDezenas,
        duques_dezenas: duquesDezenas,
        duplas_grupo: duplasGrupo,
        ternos_grupo: ternosGrupo
      };

      const { data, error } = await supabase
        .from("jogos_gerados")
        .insert(jogoGerado)
        .select()
        .single();

      if (error) throw error;

      setJogosGerados(data);
      setShowJogosModal(true);
      toast.success("Jogos gerados com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao gerar jogos:", error);
      toast.error("Erro ao gerar jogos");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const gerarMilhar = (resultados: JogoBichoResultado[], quantidade: number): string[] => {
    const milhares = new Set<string>();
    const frequencia = new Map<string, number>();

    resultados.forEach(resultado => {
      [
        resultado.ppt_1, resultado.ppt_2, resultado.ppt_3, resultado.ppt_4, resultado.ppt_5,
        resultado.ptm_1, resultado.ptm_2, resultado.ptm_3, resultado.ptm_4, resultado.ptm_5,
        resultado.pt_1, resultado.pt_2, resultado.pt_3, resultado.pt_4, resultado.pt_5,
        resultado.ptv_1, resultado.ptv_2, resultado.ptv_3, resultado.ptv_4, resultado.ptv_5,
        resultado.ptn_1, resultado.ptn_2, resultado.ptn_3, resultado.ptn_4, resultado.ptn_5,
        resultado.coruja_1, resultado.coruja_2, resultado.coruja_3, resultado.coruja_4, resultado.coruja_5
      ].forEach(valor => {
        if (valor && valor.length === 4) {
          milhares.add(valor);
          frequencia.set(valor, (frequencia.get(valor) || 0) + 1);
        }
      });
    });

    const milharesOrdenados = Array.from(milhares)
      .sort((a, b) => (frequencia.get(b) || 0) - (frequencia.get(a) || 0));

    return milharesOrdenados.slice(0, quantidade);
  };

  const gerarCentenas = (resultados: JogoBichoResultado[], quantidade: number): string[] => {
    const centenas = new Set<string>();
    const frequencia = new Map<string, number>();

    resultados.forEach(resultado => {
      [
        resultado.ppt_1, resultado.ppt_2, resultado.ppt_3, resultado.ppt_4, resultado.ppt_5,
        resultado.ptm_1, resultado.ptm_2, resultado.ptm_3, resultado.ptm_4, resultado.ptm_5,
        resultado.pt_1, resultado.pt_2, resultado.pt_3, resultado.pt_4, resultado.pt_5,
        resultado.ptv_1, resultado.ptv_2, resultado.ptv_3, resultado.ptv_4, resultado.ptv_5,
        resultado.ptn_1, resultado.ptn_2, resultado.ptn_3, resultado.ptn_4, resultado.ptn_5,
        resultado.coruja_1, resultado.coruja_2, resultado.coruja_3, resultado.coruja_4, resultado.coruja_5
      ].forEach(valor => {
        if (valor && valor.length === 4) {
          const centena = valor.substring(1, 4);
          centenas.add(centena);
          frequencia.set(centena, (frequencia.get(centena) || 0) + 1);
        }
      });
    });

    const centenasOrdenadas = Array.from(centenas)
      .sort((a, b) => (frequencia.get(b) || 0) - (frequencia.get(a) || 0));

    return centenasOrdenadas.slice(0, quantidade);
  };

  const gerarDezenas = (resultados: JogoBichoResultado[], quantidade: number): string[] => {
    const dezenas = new Set<string>();
    const frequencia = new Map<string, number>();

    resultados.forEach(resultado => {
      [
        resultado.ppt_1, resultado.ppt_2, resultado.ppt_3, resultado.ppt_4, resultado.ppt_5,
        resultado.ptm_1, resultado.ptm_2, resultado.ptm_3, resultado.ptm_4, resultado.ptm_5,
        resultado.pt_1, resultado.pt_2, resultado.pt_3, resultado.pt_4, resultado.pt_5,
        resultado.ptv_1, resultado.ptv_2, resultado.ptv_3, resultado.ptv_4, resultado.ptv_5,
        resultado.ptn_1, resultado.ptn_2, resultado.ptn_3, resultado.ptn_4, resultado.ptn_5,
        resultado.coruja_1, resultado.coruja_2, resultado.coruja_3, resultado.coruja_4, resultado.coruja_5
      ].forEach(valor => {
        if (valor && valor.length === 4) {
          const dezena = valor.substring(2, 4);
          dezenas.add(dezena);
          frequencia.set(dezena, (frequencia.get(dezena) || 0) + 1);
        }
      });
    });

    const dezenasOrdenadas = Array.from(dezenas)
      .sort((a, b) => (frequencia.get(b) || 0) - (frequencia.get(a) || 0));

    return dezenasOrdenadas.slice(0, quantidade);
  };

  const gerarTernosDezenas = (resultados: JogoBichoResultado[], quantidade: number): string[] => {
    const dezenas = gerarDezenas(resultados, 15);
    const ternos: string[] = [];

    for (let i = 0; i < dezenas.length - 2 && ternos.length < quantidade; i++) {
      for (let j = i + 1; j < dezenas.length - 1 && ternos.length < quantidade; j++) {
        for (let k = j + 1; k < dezenas.length && ternos.length < quantidade; k++) {
          ternos.push(`${dezenas[i]}-${dezenas[j]}-${dezenas[k]}`);
        }
      }
    }

    return ternos.slice(0, quantidade);
  };

  const gerarDuquesDezenas = (resultados: JogoBichoResultado[], quantidade: number): string[] => {
    const dezenas = gerarDezenas(resultados, 10);
    const duques: string[] = [];

    for (let i = 0; i < dezenas.length - 1 && duques.length < quantidade; i++) {
      for (let j = i + 1; j < dezenas.length && duques.length < quantidade; j++) {
        duques.push(`${dezenas[i]}-${dezenas[j]}`);
      }
    }

    return duques.slice(0, quantidade);
  };

  const gerarDuplasGrupo = (resultados: JogoBichoResultado[], quantidade: number): string[] => {
    const grupos = new Set<string>();
    const frequencia = new Map<string, number>();

    resultados.forEach(resultado => {
      [
        resultado.ppt_1, resultado.ppt_2, resultado.ppt_3, resultado.ppt_4, resultado.ppt_5,
        resultado.ptm_1, resultado.ptm_2, resultado.ptm_3, resultado.ptm_4, resultado.ptm_5,
        resultado.pt_1, resultado.pt_2, resultado.pt_3, resultado.pt_4, resultado.pt_5,
        resultado.ptv_1, resultado.ptv_2, resultado.ptv_3, resultado.ptv_4, resultado.ptv_5,
        resultado.ptn_1, resultado.ptn_2, resultado.ptn_3, resultado.ptn_4, resultado.ptn_5,
        resultado.coruja_1, resultado.coruja_2, resultado.coruja_3, resultado.coruja_4, resultado.coruja_5
      ].forEach(valor => {
        if (valor && valor.length === 4) {
          const dezena = valor.substring(2, 4);
          const grupo = String(Math.floor((parseInt(dezena) - 1) / 4) + 1).padStart(2, '0');
          grupos.add(grupo);
          frequencia.set(grupo, (frequencia.get(grupo) || 0) + 1);
        }
      });
    });

    const gruposArray = Array.from(grupos)
      .sort((a, b) => (frequencia.get(b) || 0) - (frequencia.get(a) || 0));

    const duplas: string[] = [];
    for (let i = 0; i < gruposArray.length - 1 && duplas.length < quantidade; i++) {
      for (let j = i + 1; j < gruposArray.length && duplas.length < quantidade; j++) {
        duplas.push(`${gruposArray[i]}-${gruposArray[j]}`);
      }
    }

    return duplas.slice(0, quantidade);
  };

  const gerarTernosGrupo = (resultados: JogoBichoResultado[], quantidade: number): string[] => {
    const grupos = new Set<string>();
    const frequencia = new Map<string, number>();

    resultados.forEach(resultado => {
      [
        resultado.ppt_1, resultado.ppt_2, resultado.ppt_3, resultado.ppt_4, resultado.ppt_5,
        resultado.ptm_1, resultado.ptm_2, resultado.ptm_3, resultado.ptm_4, resultado.ptm_5,
        resultado.pt_1, resultado.pt_2, resultado.pt_3, resultado.pt_4, resultado.pt_5,
        resultado.ptv_1, resultado.ptv_2, resultado.ptv_3, resultado.ptv_4, resultado.ptv_5,
        resultado.ptn_1, resultado.ptn_2, resultado.ptn_3, resultado.ptn_4, resultado.ptn_5,
        resultado.coruja_1, resultado.coruja_2, resultado.coruja_3, resultado.coruja_4, resultado.coruja_5
      ].forEach(valor => {
        if (valor && valor.length === 4) {
          const dezena = valor.substring(2, 4);
          const grupo = String(Math.floor((parseInt(dezena) - 1) / 4) + 1).padStart(2, '0');
          grupos.add(grupo);
          frequencia.set(grupo, (frequencia.get(grupo) || 0) + 1);
        }
      });
    });

    const gruposArray = Array.from(grupos)
      .sort((a, b) => (frequencia.get(b) || 0) - (frequencia.get(a) || 0));

    const ternos: string[] = [];
    for (let i = 0; i < gruposArray.length - 2 && ternos.length < quantidade; i++) {
      for (let j = i + 1; j < gruposArray.length - 1 && ternos.length < quantidade; j++) {
        for (let k = j + 1; k < gruposArray.length && ternos.length < quantidade; k++) {
          ternos.push(`${gruposArray[i]}-${gruposArray[j]}-${gruposArray[k]}`);
        }
      }
    }

    return ternos.slice(0, quantidade);
  };

  const clearAllResultados = async () => {
    if (!user?.id) return false;
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("jogo_bicho_resultados")
        .delete()
        .eq("user_id", user.id);
      if (error) throw error;
      toast.success("Todos os resultados foram limpos!");
      await loadResultados();
      return true;
    } catch (error) {
      console.error("Erro ao limpar resultados:", error);
      toast.error("Erro ao limpar resultados");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearResultadoByDate = async (date: string) => {
    if (!user?.id || !date) return false;
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("jogo_bicho_resultados")
        .delete()
        .eq("user_id", user.id)
        .eq("data", date);
      if (error) throw error;
      toast.success("Resultados do dia selecionado foram limpos!");
      await loadResultados();
      return true;
    } catch (error) {
      console.error("Erro ao limpar por dia:", error);
      toast.error("Erro ao limpar por dia");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    resultados,
    config,
    jogosGerados,
    isLoading,
    showJogosModal,
    setShowJogosModal,
    selectedDays,
    setSelectedDays,
    saveResultado,
    saveMultipleResultados,
    saveConfig,
    gerarJogos,
    loadResultados,
    loadConfig,
    isTrialExpired,
    clearAllResultados,
    clearResultadoByDate,
  };
};
